// VOY-485 (rebuild) — Keywords (Public profile tab) — FULL EDITOR.
//
// DECLARED INPUT. Mirrors the live profile's `KeywordsCard` — up to 5
// weighted keyword slots, add inline, star-weighted display (rank 1 = 5
// stars … rank 5 = 1 star), drag-to-reorder priority, hover-X to remove,
// debounced save via `saveKeywordPreferences` (which runs `enrichKeyword`
// → inferred_dimensions/topics + source field per VOY-438).
//
// This is the canonical "restyle-and-preserve" case: every interaction
// from the live editor exists here, only the chrome changes to v6.
//
// Note on weight semantics: the live `KeywordsCard` derives weight from
// position (5 stars for slot 1 down to 1 star for slot 5). That's an
// intentional simplification — the user perceives priority order, the
// backend stores a weight integer. We keep this; if the You page wants
// to display the explicit weight number too, the data is already there.

import { useEffect, useMemo, useRef, useState } from 'react'
import { GripVertical, Plus, Star, X, Loader2 } from 'lucide-react'
import { getKeywordPreferences, saveKeywordPreferences } from '@/lib/profile'
import type { KeywordPreference } from '@/types/profile'

const MAX_KEYWORDS = 5

export interface KeywordsSectionProps {
  userId: string
}

type Keyword = KeywordPreference

export function KeywordsSection({ userId }: KeywordsSectionProps) {
  const [keywords, setKeywords] = useState<Keyword[]>([])
  const [isAdding, setIsAdding] = useState(false)
  const [newKeyword, setNewKeyword] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    loadKeywords()
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  async function loadKeywords() {
    try {
      const data = await getKeywordPreferences(userId)
      setKeywords(data)
    } catch (error) {
      console.error('[KeywordsSection] load failed:', error)
    }
  }

  const saveKeywordsDebounced = (next: Keyword[]) => {
    setIsSaving(true)
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await saveKeywordPreferences(userId, next)
      } catch (error) {
        console.error('[KeywordsSection] save failed:', error)
      } finally {
        setIsSaving(false)
      }
    }, 700)
  }

  const commitNew = () => {
    const trimmed = newKeyword.trim()
    if (!trimmed || keywords.length >= MAX_KEYWORDS) {
      setIsAdding(false)
      setNewKeyword('')
      return
    }
    const exists = keywords.some((k) => k.keyword.toLowerCase() === trimmed.toLowerCase())
    if (exists) {
      setNewKeyword('')
      return
    }
    const next: Keyword[] = [
      ...keywords,
      { keyword: trimmed, weight: MAX_KEYWORDS - keywords.length },
    ]
    setKeywords(next)
    saveKeywordsDebounced(next)
    setNewKeyword('')
    setIsAdding(false)
  }

  const removeKeyword = (index: number) => {
    const next = keywords.filter((_, i) => i !== index).map((k, i) => ({
      ...k,
      weight: MAX_KEYWORDS - i,
    }))
    setKeywords(next)
    saveKeywordsDebounced(next)
  }

  const handleDragStart = (index: number) => setDraggedIdx(index)
  const handleDragOver = (e: React.DragEvent) => e.preventDefault()
  const handleDrop = (dropIndex: number) => {
    if (draggedIdx === null || draggedIdx === dropIndex) {
      setDraggedIdx(null)
      return
    }
    const dragged = keywords[draggedIdx]
    const rest = keywords.filter((_, i) => i !== draggedIdx)
    rest.splice(dropIndex, 0, dragged)
    const reweighted = rest.map((k, i) => ({ ...k, weight: MAX_KEYWORDS - i }))
    setKeywords(reweighted)
    saveKeywordsDebounced(reweighted)
    setDraggedIdx(null)
  }

  const slotsUsed = keywords.length

  return (
    <section className="card" aria-labelledby="keywords-h">
      <div className="card-h">
        <h3 id="keywords-h" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Keywords
          {isSaving && <Loader2 className="kw-spin" aria-hidden="true" />}
        </h3>
        {slotsUsed < MAX_KEYWORDS && !isAdding && (
          <button type="button" className="kw-add" onClick={() => setIsAdding(true)}>
            <Plus aria-hidden="true" />
            Add
          </button>
        )}
      </div>

      {keywords.length === 0 && !isAdding ? (
        <div className="kw-empty">
          <p>Add up to {MAX_KEYWORDS} keywords so AI agents know what matters to you.</p>
        </div>
      ) : (
        <ol className="kw-list">
          {keywords.map((kw, index) => (
            <KeywordRow
              key={kw.keyword}
              index={index}
              keyword={kw}
              isDragging={draggedIdx === index}
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              onRemove={() => removeKeyword(index)}
            />
          ))}
          {isAdding && (
            <li className="kw-input-row">
              <input
                autoFocus
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitNew()
                  if (e.key === 'Escape') {
                    setIsAdding(false)
                    setNewKeyword('')
                  }
                }}
                onBlur={commitNew}
                placeholder="e.g. boutique, sushi, jazz"
                className="kw-input"
              />
            </li>
          )}
        </ol>
      )}

      <p className="kw-foot">
        {slotsUsed}/{MAX_KEYWORDS} slots{slotsUsed > 0 ? ' — drag to reorder priority' : ''}
      </p>
    </section>
  )
}

function KeywordRow({
  index,
  keyword,
  isDragging,
  onDragStart,
  onDragOver,
  onDrop,
  onRemove,
}: {
  index: number
  keyword: Keyword
  isDragging: boolean
  onDragStart: () => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: () => void
  onRemove: () => void
}) {
  const stars = useMemo(() => Math.max(1, Math.min(keyword.weight, MAX_KEYWORDS)), [keyword.weight])
  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={isDragging ? 'kw-row dragging' : 'kw-row'}
    >
      <GripVertical className="kw-grip" aria-hidden="true" />
      <span className="kw-rank">{index + 1}</span>
      <span className="kw-text">{keyword.keyword}</span>
      <span className="kw-stars" aria-label={`Priority ${stars} of ${MAX_KEYWORDS}`}>
        {Array.from({ length: MAX_KEYWORDS }).map((_, i) => (
          <Star key={i} className={i < stars ? 'on' : ''} aria-hidden="true" />
        ))}
      </span>
      <button type="button" className="kw-remove" onClick={onRemove} aria-label={`Remove ${keyword.keyword}`}>
        <X aria-hidden="true" />
      </button>
    </li>
  )
}
